/**
 * Finnish municipal income tax rates (kunnallisveroprosentti), 2026.
 *
 * Source (primary): Suomen Kuntaliitto (Association of Finnish Municipalities),
 * "Kuntakohtaiset, maakuntakohtaiset ja kuntakokoluokittaiset veroprosentit 2026",
 * sheet "Kunnittain", column "2026":
 * https://www.kuntaliitto.fi/sites/default/files/media/file/Kuntakohtaiset%2C%20maakuntakohtaiset%20ja%20kuntakokoluokittaiset%20veroprosentit%202026%201.xlsx
 * (linked from https://www.kuntaliitto.fi/talous-ja-elinvoima/verotus/kuntien-veroprosentit).
 * That workbook itself cites Verohallinto (the Finnish Tax Administration) as its
 * source and is dated 18.11.2025, i.e. after municipal councils had confirmed their
 * 2026 rates for the 2026 tax year (rates must be notified to Verohallinto by
 * mid-November of the preceding year). Accessed 2026-09-05.
 *
 * Cross-checked against Kuntaliitto's companion slide deck "Kuntien veroprosentit
 * 2026" (https://www.kuntaliitto.fi/sites/default/files/media/file/Kuntien%20veroprosentit%202026%20_0.pdf,
 * accessed 2026-09-05), which states for mainland Finland: highest rate Pomarkku
 * (10.9%), lowest rate Kauniainen (4.7%), income-weighted average 7.57%. All three
 * figures match this table exactly. vero.fi's own decision notice for 2026
 * (https://www.vero.fi/syventavat-vero-ohjeet/paatokset/47465/kuntien-ja-seurakuntien-tuloveroprosentit-vuonna-2026/,
 * accessed 2026-09-05) independently confirms the 2026 rates are final/decided and
 * states a (simple, non-weighted) average of 7.60%, consistent with Kuntaliitto's
 * weighted 7.57%.
 *
 * Coverage: 308 of Finland's 309 municipalities (every municipality that had
 * notified its confirmed 2026 rate to Verohallinto as of the source's 18.11.2025
 * cut-off; the source's own header records "Ilmoittaneita: 308").
 *
 * Naming: municipality names are exactly as spelled in the Kuntaliitto source
 * (Finnish official name, e.g. bilingual municipalities appear under their Finnish
 * name only, "Maarianhamina" not "Maarianhamina - Mariehamn", "Pedersören kunta" not
 * "Pedersöre").
 *
 * Spot-check notes (verified against the citations above, not just plausibility):
 * - Helsinki / Espoo: 5.3% -- matches the well-known post-2023 hyvinvointialue
 *   (wellbeing-services-county) reform rate; both cut their nominal rate to 5.36%
 *   in 2023 and rounded down to 5.30% from 2024 onward, unchanged through 2026 in
 *   this source.
 * - Kauniainen: 4.7% -- matches Kuntaliitto's own "lowest in mainland Finland" claim.
 * - Pomarkku: 10.9% -- matches Kuntaliitto's own "highest in mainland Finland" claim.
 * - Vantaa: 6.4%, Tampere: 7.6%, Turku: 7.1%, Oulu: 8.1% -- all within the expected
 *   post-reform band for large cities and consistent with public reporting.
 * - The Åland municipalities (Maarianhamina 17.0%, Brändö 17.7%, Kökar 19.7%, etc.)
 *   are 2-4x the mainland rates. This is NOT a transcription error: Åland is
 *   self-governing and was not folded into the 2023 mainland hyvinvointialue
 *   reform that moved health/social-care funding (and ~13 percentage points of
 *   municipal tax) off municipal budgets, so Åland municipalities still fund those
 *   services through municipal tax and have correspondingly much higher nominal
 *   rates. The 17-19.7% range lines up with historical Åland rates pre-dating the
 *   mainland reform.
 */
export const FINNISH_MUNICIPAL_TAX_RATES_2026: Record<string, number> = {
  'Akaa': 9.9,
  'Alajärvi': 9.1,
  'Alavieska': 9.3,
  'Alavus': 9.6,
  'Asikkala': 8.1,
  'Askola': 9.2,
  'Aura': 8.9,
  'Brändö': 17.7,
  'Eckerö': 18.5,
  'Enonkoski': 8.4,
  'Enontekiö': 8.7,
  'Espoo': 5.3,
  'Eura': 9.4,
  'Eurajoki': 6.4,
  'Evijärvi': 9.8,
  'Finström': 19,
  'Forssa': 8.5,
  'Föglö': 18.5,
  'Geta': 18.5,
  'Haapajärvi': 10.2,
  'Haapavesi': 9.4,
  'Hailuoto': 8.9,
  'Halsua': 10.5,
  'Hamina': 9.4,
  'Hammarland': 18,
  'Hankasalmi': 9.4,
  'Hanko': 9.1,
  'Harjavalta': 8.9,
  'Hartola': 9.2,
  'Hattula': 8.4,
  'Hausjärvi': 9.3,
  'Heinola': 8.2,
  'Heinävesi': 9.5,
  'Helsinki': 5.3,
  'Hirvensalmi': 7.4,
  'Hollola': 8.3,
  'Huittinen': 9.4,
  'Humppila': 9.8,
  'Hyrynsalmi': 9,
  'Hyvinkää': 7.7,
  'Hämeenkyrö': 10.2,
  'Hämeenlinna': 8.4,
  'Ii': 8.9,
  'Iisalmi': 7.9,
  'Iitti': 8.6,
  'Ikaalinen': 9.9,
  'Ilmajoki': 9.2,
  'Ilomantsi': 8.6,
  'Imatra': 8.6,
  'Inari': 6.4,
  'Inkoo': 8.9,
  'Isojoki': 9.8,
  'Isokyrö': 9.5,
  'Janakkala': 8.9,
  'Joensuu': 8.1,
  'Jokioinen': 9,
  'Jomala': 17.5,
  'Joroinen': 8.6,
  'Joutsa': 9,
  'Juuka': 8.5,
  'Juupajoki': 8.4,
  'Juva': 9.1,
  'Jyväskylä': 8.1,
  'Jämijärvi': 10,
  'Jämsä': 9.4,
  'Järvenpää': 7.8,
  'Kaarina': 7.6,
  'Kaavi': 9.9,
  'Kajaani': 8.4,
  'Kalajoki': 8.3,
  'Kangasala': 9.4,
  'Kangasniemi': 9.4,
  'Kankaanpää': 9.1,
  'Kannonkoski': 9.2,
  'Kannus': 9.7,
  'Karijoki': 9.8,
  'Karkkila': 9.1,
  'Karstula': 9,
  'Karvia': 8.9,
  'Kaskinen': 10.3,
  'Kauhajoki': 9.6,
  'Kauhava': 9.1,
  'Kauniainen': 4.7,
  'Kaustinen': 9.9,
  'Keitele': 8.9,
  'Kemi': 9.6,
  'Kemijärvi': 8.9,
  'Keminmaa': 8.6,
  'Kemiönsaari': 7.1,
  'Kempele': 8.9,
  'Kerava': 7.3,
  'Keuruu': 9.1,
  'Kihniö': 9.4,
  'Kinnula': 9.5,
  'Kirkkonummi': 7.1,
  'Kitee': 8.1,
  'Kittilä': 7.3,
  'Kiuruvesi': 9.5,
  'Kivijärvi': 9.1,
  'Kokemäki': 9.2,
  'Kokkola': 9.2,
  'Kolari': 8.4,
  'Konnevesi': 9.8,
  'Kontiolahti': 8.8,
  'Korsnäs': 9.2,
  'Koski Tl': 7.4,
  'Kotka': 9.4,
  'Kouvola': 8.9,
  'Kristiinankaupunki': 8.9,
  'Kruunupyy': 8.9,
  'Kuhmo': 9.4,
  'Kuhmoinen': 9.1,
  'Kumlinge': 19.3,
  'Kuopio': 8.1,
  'Kuortane': 8.4,
  'Kurikka': 8.4,
  'Kustavi': 5.3,
  'Kuusamo': 8.6,
  'Kyyjärvi': 9.9,
  'Kärkölä': 9.4,
  'Kärsämäki': 9.5,
  'Kökar': 19.7,
  'Lahti': 8.6,
  'Laihia': 9.6,
  'Laitila': 8.4,
  'Lapinjärvi': 8.9,
  'Lapinlahti': 9.4,
  'Lappajärvi': 9.4,
  'Lappeenranta': 8.3,
  'Lapua': 8.9,
  'Laukaa': 9.9,
  'Lemi': 9.9,
  'Lemland': 17.8,
  'Lempäälä': 8.4,
  'Leppävirta': 8.4,
  'Lestijärvi': 9.4,
  'Lieksa': 8.4,
  'Lieto': 7.1,
  'Liminka': 9.4,
  'Liperi': 9.5,
  'Lohja': 7.9,
  'Loimaa': 8.4,
  'Loppi': 8.9,
  'Loviisa': 8.1,
  'Luhanka': 5.9,
  'Lumijoki': 8.9,
  'Lumparland': 19.5,
  'Luoto': 8.3,
  'Luumäki': 9.3,
  'Maalahti': 8.9,
  'Maarianhamina': 17,
  'Marttila': 8.8,
  'Masku': 8.5,
  'Merijärvi': 10.1,
  'Merikarvia': 8.9,
  'Miehikkälä': 10.1,
  'Mikkeli': 9.4,
  'Muhos': 9.4,
  'Multia': 9.8,
  'Muonio': 8.9,
  'Mustasaari': 8.9,
  'Muurame': 6.9,
  'Mynämäki': 9.1,
  'Myrskylä': 9.9,
  'Mäntsälä': 8.3,
  'Mänttä-Vilppula': 9.9,
  'Mäntyharju': 8.5,
  'Naantali': 6.4,
  'Nakkila': 9.1,
  'Nivala': 9.9,
  'Nokia': 8.4,
  'Nousiainen': 9.1,
  'Nurmes': 8.9,
  'Nurmijärvi': 7.9,
  'Närpiö': 8.4,
  'Orimattila': 8.7,
  'Oripää': 8.4,
  'Orivesi': 9.4,
  'Oulainen': 10,
  'Oulu': 8.1,
  'Outokumpu': 8.9,
  'Padasjoki': 8.4,
  'Paimio': 8.2,
  'Paltamo': 9.4,
  'Parainen': 7.9,
  'Parikkala': 9.5,
  'Parkano': 9.4,
  'Pedersören kunta': 9.3,
  'Pelkosenniemi': 9.1,
  'Pello': 9.5,
  'Perho': 9.3,
  'Petäjävesi': 9.9,
  'Pieksämäki': 9.4,
  'Pielavesi': 9.1,
  'Pietarsaari': 9,
  'Pihtipudas': 8.9,
  'Pirkkala': 7.9,
  'Polvijärvi': 9,
  'Pomarkku': 10.9,
  'Pori': 8.7,
  'Pornainen': 8.3,
  'Porvoo': 7.1,
  'Posio': 9.1,
  'Pudasjärvi': 9.5,
  'Pukkila': 10.4,
  'Punkalaidun': 9.4,
  'Puolanka': 8.9,
  'Puumala': 6.6,
  'Pyhtää': 8.1,
  'Pyhäjoki': 7.9,
  'Pyhäjärvi': 9.1,
  'Pyhäntä': 8,
  'Pyhäranta': 9.1,
  'Pälkäne': 8.9,
  'Pöytyä': 8.6,
  'Raahe': 9.1,
  'Raasepori': 9.3,
  'Raisio': 7.6,
  'Rantasalmi': 9.4,
  'Ranua': 8.1,
  'Rauma': 7.9,
  'Rautalampi': 9.9,
  'Rautavaara': 9.4,
  'Rautjärvi': 8.5,
  'Reisjärvi': 10.2,
  'Riihimäki': 7.9,
  'Ristijärvi': 9.3,
  'Rovaniemi': 8.9,
  'Ruokolahti': 8.6,
  'Ruovesi': 9.4,
  'Rusko': 7.3,
  'Rääkkylä': 9.9,
  'Saarijärvi': 9.3,
  'Salla': 8.9,
  'Salo': 8.1,
  'Saltvik': 18.5,
  'Sastamala': 8.9,
  'Sauvo': 8.8,
  'Savitaipale': 8.9,
  'Savonlinna': 9.3,
  'Savukoski': 9.1,
  'Seinäjoki': 9.2,
  'Sievi': 9.6,
  'Siikainen': 9.4,
  'Siikajoki': 9.4,
  'Siikalatva': 9.3,
  'Siilinjärvi': 9.4,
  'Simo': 9.4,
  'Sipoo': 6.8,
  'Siuntio': 8.6,
  'Sodankylä': 7.5,
  'Soini': 9.1,
  'Somero': 8.5,
  'Sonkajärvi': 8.6,
  'Sotkamo': 8.5,
  'Sottunga': 18,
  'Sulkava': 8.4,
  'Sund': 19,
  'Suomussalmi': 8.9,
  'Suonenjoki': 9.1,
  'Sysmä': 6.4,
  'Säkylä': 8.9,
  'Taipalsaari': 8.4,
  'Taivalkoski': 8.5,
  'Taivassalo': 6.9,
  'Tammela': 8.9,
  'Tampere': 7.6,
  'Tervo': 9.9,
  'Tervola': 6.9,
  'Teuva': 9.6,
  'Tohmajärvi': 9.1,
  'Toholampi': 9.9,
  'Toivakka': 9.4,
  'Tornio': 8.7,
  'Turku': 7.1,
  'Tuusniemi': 9.9,
  'Tuusula': 7.3,
  'Tyrnävä': 9.9,
  'Ulvila': 9.4,
  'Urjala': 10.3,
  'Utajärvi': 8.4,
  'Utsjoki': 8.9,
  'Uurainen': 9.2,
  'Uusikaarlepyy': 8.6,
  'Uusikaupunki': 8.9,
  'Vaala': 8.3,
  'Vaasa': 8.4,
  'Valkeakoski': 9.1,
  'Vantaa': 6.4,
  'Varkaus': 9.3,
  'Vehmaa': 9.1,
  'Vesanto': 9.5,
  'Vesilahti': 9.3,
  'Veteli': 9.8,
  'Vieremä': 8.4,
  'Vihti': 7.8,
  'Viitasaari': 8.4,
  'Vimpeli': 9.8,
  'Virolahti': 10.1,
  'Virrat': 8.6,
  'Vårdö': 19,
  'Vöyri': 9.2,
  'Ylitornio': 8.4,
  'Ylivieska': 10.3,
  'Ylöjärvi': 9.2,
  'Ypäjä': 9.3,
  'Ähtäri': 10.6,
  'Äänekoski': 9.4,
};
